/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2022, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Injectable, Injector } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { DiService } from '@app/di/di.service';
import { Logger, MessageService } from '@core/lib/services';
import { DiConfigService } from '@app/di/di-config.service';
import { appConstants } from '@app/common/app-constants';
import { UtilService } from '@app/util/util.service';
import { ResponseInfo } from '@app/di/data-model/response-info';
import { isEmpty, isValidValue } from '@core/lib/helpers/helpers';
import { AuthenticationService } from '@app/core/authentication';
import { LoginService } from '@app/authentication/login/login.service';
import { SignOutService } from '@app/common/sign-out.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { LineShaftIntegration, LineShaftHandlerService } from '@app/di/channel/lineshaft/line-shaft-handler.service';
import { IntegrationStatusService } from '@app/di/integration-status.service';
import { Subscription } from 'rxjs';

const log = new Logger('WfmContextService');

@Injectable({
  providedIn: 'root'
})
export class WfmContextService implements LineShaftIntegration {
  private lineShaftHandlerService: LineShaftHandlerService;
  private subscription: Subscription[] = [];
  private WFM_CONTEXT_CONFIG;
  private VIEWER_CONTEXT_CONFIG;
  private UVIEWER_CONFIG;
  private isGenericExtendInboundSet: boolean;
  private isReportingEventsEnabled: boolean;

  messageId: string;

  constructor(
    private injector: Injector,
    private utilService: UtilService,
    private appConfig: AppConfigService,
    private xmlTemplateManagerService: XmlTemplateManagerService,
    private diConfigService: DiConfigService,
    private diService: DiService,
    private authenticationService: AuthenticationService,
    private messageService: MessageService,
    private loginService: LoginService,
    private signOutService: SignOutService,
    private notify: NotifyService,
    private translate: TranslateService,
    private integrationStatusService: IntegrationStatusService
  ) {}

  // Service initialization
  init(isExternalLaunch = false, message?) {
    this.isGenericExtendInboundSet = this.appConfig.getAppConfigByKey('genericExtendInbound') === 'true';
    this.isReportingEventsEnabled = this.appConfig.getAppConfigByKey('enableReportingEvents') === 'true';

    // For now ignoring the checks until solution for WFM single instance workflow without lineshaft is clarified.
    this.initialize(isExternalLaunch, message);
  }

  private initialize(isExternalLaunch, message) {
    log.info('App Initializing...');
    // Communication channel
    this.lineShaftHandlerService = this.injector.get(LineShaftHandlerService);
    this.lineShaftHandlerService.registerCleanupCallback(this.cleanup);

    // Configs
    this.WFM_CONTEXT_CONFIG = this.diConfigService.WFM_CONTEXT_CONFIG;
    this.VIEWER_CONTEXT_CONFIG = this.diConfigService.VIEWER_CONTEXT_CONFIG;
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;

    // Listeners
    this.lineShaftHandlerService.channelObservers.onResponseMessage.subscribe(({ responseInfo, _responseXML }) => {
      log.debug('LineShaft message processing', responseInfo);
      this.handleResponse(responseInfo);
      this.handlePluginResponse(responseInfo);
    });
    if (isExternalLaunch) {
      const loadObserver = this.lineShaftHandlerService.channelObservers.onReady.subscribe(startLoad => {
        if (startLoad) {
          log.info('LineShaft registration started for authorization flow (meant for External Launch)');
          this.loadPluginForExternalLaunchWorkflow();
          loadObserver.unsubscribe();
        }
      });
    } else {
      this.messageId = message;
      this.subscription.push(
        this.lineShaftHandlerService.channelObservers.onReady.subscribe(startLoad => {
          if (startLoad) {
            log.info('LineShaft registration started');
            this.initializeLineShaftWFMContextPlugin();
          }
        })
      );
    }
  }

  // Channel message handler
  handleResponse(responseInfo: ResponseInfo) {
    if (responseInfo.sourceName === this.WFM_CONTEXT_CONFIG.MODULENAME) {
      if (
        responseInfo.actionType === appConstants.wfmContextPlugin.actionTypes.REQUEST_TOKEN &&
        isValidValue(responseInfo.jwtToken)
      ) {
        this.messageId = responseInfo.messageId;
        this.validateToken(responseInfo.jwtToken);
      } else if (responseInfo.actionType === appConstants.wfmContextPlugin.actionTypes.LOGOUT) {
        this.messageId = responseInfo.messageId;
        this.signOutService.signOut(true);
      } else if (responseInfo.actionType === appConstants.wfmContextPlugin.actionTypes.SET_TOKEN) {
        this.messageId = responseInfo.messageId;
        this.setToken(responseInfo.jwtToken);
      } else if (responseInfo.actionType === appConstants.wfmContextPlugin.actionTypes.SET_PATIENT) {
        this.messageId = responseInfo.messageId;
        const mrn = isValidValue(responseInfo.mrn) ? responseInfo.mrn.toString() : '';
        this.messageService.broadcast('setPatientByExternalApp', responseInfo.mrn);
        if (isEmpty(mrn)) {
          this.messageService.broadcast('displayDefaultWorkListByExternalApp');
        }
        this.sendRequest(
          appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE,
          appConstants.wfmContextPlugin.actionTypes.SET_PATIENT,
          appConstants.wfmContextPlugin.statusTypes.SUCCESS
        );
      }
    }
  }
  handlePluginResponse(responseInfo: ResponseInfo) {
    if (
      responseInfo.moduleName === this.WFM_CONTEXT_CONFIG.MODULENAME &&
      responseInfo.topicName === 'RadDesktop.LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === 'LineShaftPluginManager' &&
      responseInfo.resultValue === 'FAILURE'
    ) {
      log.error('LSPluginManagerResponseHandler - Load plugin failed');
      this.handleWFMContextPluginLoadFailureFromPM();
    }

    if (
      responseInfo.topicName === 'LS_PLUGIN_LOAD_RESPONSE' &&
      responseInfo.sourceName === this.WFM_CONTEXT_CONFIG.MODULENAME
    ) {
      log.info('handleResponse :' + responseInfo);
    }
  }

  private validateToken(jwtToken: string) {
    this.authenticationService
      .validateJwtToken(jwtToken)
      .then(successResponse => {
        this.messageService.broadcast('jwtValidation.success', successResponse);
        if (successResponse.userName) {
          this.loginService.username = successResponse.userName;
        }
        this.loginService.navigateToLoginSuccessPage();
      })
      .catch(ex => {
        this.sendRequest(
          appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE,
          appConstants.wfmContextPlugin.actionTypes.LAUNCH,
          appConstants.wfmContextPlugin.statusTypes.TOKEN_INVALID
        );
        this.messageService.broadcast('jwtValidation.error', ex);
      });
  }

  private setToken(jwtToken: string) {
    this.authenticationService
      .validateJwtToken(jwtToken)
      .then(() => {
        this.sendRequest(
          appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE,
          appConstants.wfmContextPlugin.actionTypes.SET_TOKEN,
          appConstants.wfmContextPlugin.statusTypes.SUCCESS
        );
      })
      .catch(error => {
        this.sendRequest(
          appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE,
          appConstants.wfmContextPlugin.actionTypes.SET_TOKEN,
          appConstants.wfmContextPlugin.statusTypes.FAILURE
        );
        log.error('Error occurred while renew token', error);
        this.setTokenError(error);
      });
  }

  cleanup = (): boolean => {
    try {
      log.info('CleanUp - Started');
      const wfmContextCloseXML = this.xmlTemplateManagerService.getPluginCloseXml(this.WFM_CONTEXT_CONFIG.MODULENAME);
      if (this.utilService.externalLoginApp) {
        this.sendRequest(
          appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE,
          appConstants.wfmContextPlugin.actionTypes.LOGOUT,
          appConstants.wfmContextPlugin.statusTypes.SUCCESS
        );
        const timeout = setTimeout(() => {
          clearTimeout(timeout);
          this.diService.invokeAPIWrapperExecute(wfmContextCloseXML);
        }, 200);
      } else {
        this.diService.invokeAPIWrapperExecute(wfmContextCloseXML);
      }
      return true;
    } catch (error) {
      log.error('CleanUp - Error >> ', error);
      return false;
    }
  };

  private setTokenError(error: HttpErrorResponse) {
    if (error !== null && error.status >= 400) {
      log.error(
        'Service Error for  while renew token' +
          ' : with status' +
          error.status +
          ' - status text : ' +
          error.statusText +
          ', Message - ' +
          error.message
      );

      if (error.status === 401) {
        //sign out and login again  when getting 401 error from backend
        this.notify
          .decision(this.translate.instant('imagingDesktop.error.sessionDisConnect.ccwWorkflow'), {
            headerContent: this.translate.instant('imagingDesktop.error.sessiontimeout.Title'),
            content: this.translate.instant('imagingDesktop.error.sessionDisConnect.ccwWorkflow'),
            yesButtonLabel: this.translate.instant('label.OK'),
            disableHeaderCloseBtn: true,
            isDecisionModal: false,
            enableYesBtn: true
          })
          .then(() => {
            localStorage.currentUserLoggedIn = 'false';
            this.messageService.broadcast('signOutOnSessionTimeout');
          });
      }
    }
  }

  /** Section : Utility functions */
  sendRequest(topic: string, actionType: string, status: string): void {
    const requestXml = this.xmlTemplateManagerService.getWfmContextRequestXml(
      topic,
      actionType,
      this.messageId,
      status
    );
    if (topic === appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE) {
      this.messageId = '0';
    }
    this.diService.invokeAPIWrapperExecute(requestXml);
  }

  subscribePlugin(topic: string, moduleName: string) {
    const subscribeXml = this.xmlTemplateManagerService.getSubscribeXml(topic, moduleName);
    this.diService.loadPluginsInvokeAPIWrapperExecute(subscribeXml);
  }

  /** Section : Plugin load & response subscribe functions */
  // External launch workflow
  private subscribeExternalLaunchPluginResponses() {
    // Subscribe the load response from WFMContext plugin
    this.subscribePlugin('LS_PLUGIN_LOAD_RESPONSE', this.WFM_CONTEXT_CONFIG.MODULENAME);
    log.info('Subscribe the load response from ViewerContext plugin');

    // Subscribe response from LineShaftPluginManager to get load response error due to the missing
    // of WFMContext
    this.subscribePlugin(this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_LOAD_RESPONSE', 'LineShaftPluginManager');

    log.info('Subscribe the load response from WFMContext plugin for WFM_APP_RESPONSE for external launch integration');
    this.subscribePlugin(
      this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.' + appConstants.wfmContextPlugin.topics.WFM_APP,
      this.WFM_CONTEXT_CONFIG.MODULENAME
    );
    this.subscribePlugin(
      this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.' + appConstants.wfmContextPlugin.topics.WFM_APP_RESPONSE,
      this.WFM_CONTEXT_CONFIG.MODULENAME
    );
    log.info('Subscribe the load response from WFMContext plugin for WFM_API_RESPONSE for external launch integration');
    this.subscribePlugin(
      this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.' + appConstants.wfmContextPlugin.topics.WFM_API,
      this.WFM_CONTEXT_CONFIG.MODULENAME
    );
    this.subscribePlugin(
      this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.' + appConstants.wfmContextPlugin.topics.WFM_API_RESPONSE,
      this.WFM_CONTEXT_CONFIG.MODULENAME
    );
  }

  private load(): boolean {
    try {
      const wfmContextLoadXML = this.xmlTemplateManagerService.getPluginLoadXml(this.WFM_CONTEXT_CONFIG.MODULENAME);
      log.info('load WFM Context plugin');
      const wfmContextLoadMessage = this.diService.getLSMessage(
        this.diService.getLSMessageType().LOAD,
        wfmContextLoadXML
      );
      wfmContextLoadMessage.responseSource = [this.UVIEWER_CONFIG.PLUGIN_MANAGER];
      wfmContextLoadMessage.moduleName = this.WFM_CONTEXT_CONFIG.MODULENAME;
      this.diService.loadPluginsInvokeAPIWrapper(wfmContextLoadMessage);
      return true;
    } catch (errorLoadWFMContextPlugin) {
      log.error('loadPlugin() WFMContextPlugin load failed :' + errorLoadWFMContextPlugin);
    }
    return false;
  }

  private loadPluginForExternalLaunchWorkflow() {
    try {
      this.subscribeExternalLaunchPluginResponses();
      if (this.load()) {
        this.sendRequest(
          appConstants.wfmContextPlugin.topics.WFM_APP,
          appConstants.wfmContextPlugin.actionTypes.REQUEST_TOKEN,
          undefined
        );
      }
    } catch (errorSubscribeWFMContextPlugin) {
      log.error('loadPluginForExternalLaunchWorkflow() WFMContextPlugin :' + errorSubscribeWFMContextPlugin);
    }
  }

  // Normal workflow
  subscribePluginResponse(retObj: any) {
    if (this.isGenericExtendInboundSet) {
      try {
        // Subscribe the load response from WFMContext plugin
        this.subscribePlugin('LS_PLUGIN_LOAD_RESPONSE', this.WFM_CONTEXT_CONFIG.MODULENAME);
        log.info('subscribePluginResponse - Subscribe the load response from ViewerContext plugin');

        // Subscribe response from LineShaftPluginManager to get load response error due to the missing
        // of WFMContext
        this.subscribePlugin(
          this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_LOAD_RESPONSE',
          'LineShaftPluginManager'
        );

        log.info('subscribePluginResponse - Subscribe the setcontext response from WFMContextPlugin');
        this.subscribePlugin(
          this.WFM_CONTEXT_CONFIG.OWNERINSTANCE + '.LS_PLUGIN_SETCONTEXT_RESPONSE',
          this.WFM_CONTEXT_CONFIG.MODULENAME
        );

        log.info('Subscribe the setcontext response from WFMContextPlugin');
        this.subscribePlugin('LS_WFMCONTEXT_REQUEST', this.WFM_CONTEXT_CONFIG.MODULENAME);
      } catch (errorSubscribeWFMContextPlugin) {
        log.error('subscribePluginResponse() WFMContextPlugin :' + errorSubscribeWFMContextPlugin);
        retObj.loadErrorEntity.push('WFMContext');
      }
    }

    try {
      log.info('Subscribe the load response from ViewerContext plugin');
      this.subscribePlugin(
        'LS_PLUGIN_LOAD_RESPONSE',
        this.VIEWER_CONTEXT_CONFIG.MODULENAME + '*' + this.VIEWER_CONTEXT_CONFIG.MODULENAME
      );
    } catch (errorSubscribeWFMContextPlugin) {
      retObj.loadErrorEntity.push('WFMContextPlugin');
      log.error('subscribePluginResponse() ViewerContextPlugin :' + errorSubscribeWFMContextPlugin);
    }

    // External launch integration related subscriptions
    if (this.utilService.externalLoginApp) {
      try {
        this.subscribeExternalLaunchPluginResponses();
      } catch (errorSubscribeWFMContextPlugin) {
        retObj.loadErrorEntity.push('WFMContextPlugin');
        log.error('subscribePluginResponse() ViewerContextPlugin :' + errorSubscribeWFMContextPlugin);
      }
    }
  }

  loadPlugin(retObj?: any) {
    if (
      this.isReportingEventsEnabled ||
      (this.isGenericExtendInboundSet && !_.contains(retObj.loadErrorEntity, 'WFMContext'))
    ) {
      this.load();
    }
  }

  private initializeLineShaftWFMContextPlugin() {
    this.utilService.loopUntilSuccessOrMaxRetry((lastAttempt: boolean) => {
      const retObj = { loadErrorEntity: [] };
      this.subscribePluginResponse(retObj);
      this.loadPlugin(retObj);

      if (lastAttempt && retObj.loadErrorEntity.length !== 0) {
        // If Viewer plugin load failed, display critical error message and remove Viewer from error list
        log.error('loadPluginHandler - Viewer plugin load failed');
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.loadViewerFailed'),
          this.translate.instant('di.desktopIntegration.titleViewerDisabled')
        );
      }
      return retObj.loadErrorEntity.length === 0;
    }, this.WFM_CONTEXT_CONFIG.MODULENAME);
  }

  /**
   * Method to handle load failure from LS plugin manager.
   * responseInfo - response information
   */
  private displayNotificationMsgDetailsInSystemStatus(key, message) {
    this.appConfig.getSystemStatusNotifier().next({ key, message });
  }

  private handleWFMContextPluginLoadFailureFromPM() {
    this.displayNotificationMsgDetailsInSystemStatus(
      'keyWFMContextPluginLoadFail',
      this.translate.instant('systemStatusMsg.wfmContextPluginLoadFail')
    );
    log.error(
      'handleWFMContextPluginLoadFailureFromPM - WFMContext plugin load failed and ' +
        'WFMContext plugin integration disabled'
    );
    if (!this.integrationStatusService.getWFMContextPluginStatus()) {
      this.notify.critical(
        this.translate.instant('di.desktopIntegration.critical.loadGenExFailed'),
        this.translate.instant('di.desktopIntegration.titleGenExDisabled')
      );
    }
    this.integrationStatusService.setWFMContextPluginStatus(true);
  }
}
